"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { alpha, styled, useTheme } from "@mui/material/styles";

import {
  Refresh,
  Close,
  Search,
  FilterList,
  Clear,
  Visibility,
  CheckCircle,
  Cancel,
  WarningAmber,
  Save,
} from "@mui/icons-material";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useBranch } from "./../../layouts/BranchContext";

/* =========================================================
   CUSTOM TABLE SCROLLBAR
========================================================= */

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    height: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* Scrollable modal bodies use the same scrollbar. */

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* =========================================================
   API
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

const EXPENSE_LIST_API = "/library/expense/list/";
const EXPENSE_DETAIL_API = "/library/expense/detail/";
const EXPENSE_APPROVE_API = "/library/expense/approve/";
const EXPENSE_REJECT_API = "/library/expense/reject/";
const CATEGORY_LIST_API = "/library/expense-category/list/";
const PAYMENT_METHOD_API = "/master/payment-methods/";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/* =========================================================
   ACCESS TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") return null;

  const keys = ["accessToken", "access_token", "token"];

  for (const key of keys) {
    const localToken = localStorage.getItem(key);

    if (localToken) return localToken;

    const sessionToken = sessionStorage.getItem(key);

    if (sessionToken) return sessionToken;
  }

  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   USER
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") return null;

  try {
    const storedUser =
      sessionStorage.getItem("user") || localStorage.getItem("user");

    if (!storedUser) return null;

    return JSON.parse(storedUser);
  } catch (error) {
    console.error("User parse error:", error);

    return null;
  }
};

/* =========================================================
   RESPONSE HELPERS
========================================================= */

const getArray = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.data)) return data.data;

  if (Array.isArray(data?.results)) return data.results;

  if (Array.isArray(data?.expenses)) return data.expenses;

  if (Array.isArray(data?.categories)) return data.categories;

  return [];
};

/* =========================================================
   COMPONENT
========================================================= */

function ExpenseApproval() {
  const theme = useTheme();

  /* Global branch selector — replaces the local branch
     dropdown and fetchOrganizationBranches call. */

  const { selectedBranchId } = useBranch();

  const isDark = theme.palette.mode === "dark";

  const textPrimary = theme.palette.text.primary;

  const textSecondary = theme.palette.text.disabled;

  const borderColor = theme.palette.divider;

  const inputBackground = theme.palette.background.paper;

  /* =======================================================
     USER
  ======================================================= */

  const [currentUser, setCurrentUser] = useState(null);

  const [organizationId, setOrganizationId] = useState("");

  /* =======================================================
     DATA
  ======================================================= */

  const [expenses, setExpenses] = useState([]);

  const [categories, setCategories] = useState([]);

  /* =======================================================
     LOADING
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [loadingCategories, setLoadingCategories] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);

  const [paymentMethods, setPaymentMethods] = useState([]);
const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  /* Shows the table loader from first render until the
     initial fetch completes. */

  const [initialLoading, setInitialLoading] = useState(true);

  /* =======================================================
     SEARCH — client-side
  ======================================================= */

  const [searchText, setSearchText] = useState("");

  /* =======================================================
     FILTERS
     list params (per Swagger): organization, branch,
     category, payment_mode, status,
     expense_date_from, expense_date_to
  ======================================================= */

  const initialFilters = {
    category: "",
    payment_mode: "",
    status: "",
    date_from: "",
    date_to: "",
  };

  const [filters, setFilters] = useState(initialFilters);

  const [tempFilters, setTempFilters] = useState(initialFilters);

  const [filterOpen, setFilterOpen] = useState(false);

  /* =======================================================
     PAGINATION (client-side)
  ======================================================= */

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* =======================================================
     DETAIL
  ======================================================= */

  const [selectedExpense, setSelectedExpense] = useState(null);

  const [detailLoading, setDetailLoading] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);

  /* =======================================================
     APPROVE / REJECT
  ======================================================= */

  const [approveOpen, setApproveOpen] = useState(false);

  const [approveRemarks, setApproveRemarks] = useState("");

  const [rejectOpen, setRejectOpen] = useState(false);

  const [rejectionReason, setRejectionReason] = useState("");

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* =======================================================
     STYLES
  ======================================================= */

  const dialogPaperStyles = {
    backgroundColor: theme.palette.background.paper,
    color: textPrimary,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: isDark
      ? "0 20px 50px rgba(0,0,0,0.45)"
      : "0 20px 50px rgba(0,0,0,0.12)",
  };

   const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      color: textPrimary,
      backgroundColor: inputBackground,
      "& fieldset": { borderColor },
      "&:hover fieldset": { borderColor: theme.palette.primary.main },
      "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
    },
    "& .MuiInputBase-input": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: textPrimary,
      opacity: 0.6,
    },
    "& .MuiInputBase-input::placeholder": {
      color: textPrimary,
      opacity: 0.75,
    },
    "& input[type='date']::-webkit-calendar-picker-indicator": {
      filter: isDark ? "invert(1)" : "none",
      cursor: "pointer",
    },
    "& .MuiInputLabel-root": { color: textPrimary },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  };

    const selectStyles = {
    borderRadius: 2,
    backgroundColor: inputBackground,
    color: textPrimary,
    "& .MuiOutlinedInput-notchedOutline": { borderColor },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": { color: textPrimary },
    "& .MuiSelect-select": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },
    "&.Mui-disabled .MuiSelect-select": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
      opacity: 0.6,
    },
  };

    const selectLabelStyles = {
    "& .MuiInputLabel-root": { color: textPrimary, opacity: 0.85 },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
      opacity: 1,
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: textPrimary,
        border: `1px solid ${theme.palette.divider}`,
        maxHeight: 300,
        "& .MuiMenuItem-root": {
          color: textPrimary,
          "&:hover": { backgroundColor: theme.palette.action.hover },
          "&.Mui-selected": {
            backgroundColor: isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.16)"
                : "rgba(0,0,0,0.12)",
            },
          },
        },
      },
    },
  };

  /* =======================================================
     ERROR HELPERS
  ======================================================= */

  const formatFieldLabel = (field) => {
    const labels = {
      remarks: "Remarks",
      rejection_reason: "Rejection Reason",
      status: "Status",
      expense_date: "Expense Date",
      category: "Category",
      organization: "Organization",
      branch: "Branch",
    };

    return (
      labels[field] ||
      field
        ?.replaceAll("_", " ")
        ?.replace(/\b\w/g, (char) => char.toUpperCase())
    );
  };

  const getErrorMessage = (error) => {
    if (error?.response?.status === 401) {
      return "Unauthorized. Please login again.";
    }

    const backendErrors = error?.response?.data?.errors;

    if (
      backendErrors &&
      typeof backendErrors === "object" &&
      Object.keys(backendErrors).length > 0
    ) {
      return Object.entries(backendErrors)
        .map(([field, messages]) => {
          const message = Array.isArray(messages)
            ? messages.join(" ")
            : typeof messages === "string"
            ? messages
            : Object.values(messages)
                .flat(Infinity)
                .map((item) => String(item))
                .join(" ");

          return `${formatFieldLabel(field)}: ${message}`;
        })
        .join(" | ");
    }

    if (error?.response?.data?.detail) {
      return error.response.data.detail;
    }

    if (error?.response?.data?.message) {
      return error.response.data.message;
    }

    return error?.message || "Something went wrong.";
  };

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const showSnackbar = useCallback(
    (message, severity = "success") => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /* =======================================================
     READ LOGGED-IN USER (organization only — branch now
     comes from the global selector)
  ======================================================= */

  useEffect(() => {
    const user = getStoredUser();

    if (!user) {
      console.warn("Logged-in user not found.");
      return;
    }

    setCurrentUser(user);

    const orgId = user.organization_id || user.organization || "";

    setOrganizationId(orgId ? String(orgId) : "");
  }, []);

  /* =======================================================
     FETCH CATEGORIES
     GET /library/expense-category/list/
     Branch-scoped (same as the Expense Category page):
     params: organization (int), branch (int),
             is_active (boolean)
  ======================================================= */

  const fetchCategories = useCallback(async () => {
    if (!organizationId) {
      setCategories([]);
      return;
    }

    setLoadingCategories(true);

    try {
      const params = {
        organization: Number(organizationId),
        is_active: true,
      };

      if (selectedBranchId) {
        params.branch = Number(selectedBranchId);
      }

      const response = await api.get(CATEGORY_LIST_API, { params });

      const list = getArray(response);

      setCategories(
        list.map((item) => ({
          ...item,
          id: item.id ?? item.category_id ?? item.value,
          category_name: item.category_name || item.name || item.label || "",
        }))
      );
    } catch (error) {
      console.error("Category List Error:", error);

      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, [organizationId, selectedBranchId]);

  const fetchPaymentMethods = useCallback(async () => {
  setLoadingPaymentMethods(true);

  try {
    const response = await api.get(PAYMENT_METHOD_API);

    const list = getArray(response)
      .filter((item) => item.is_active !== false)
      .map((item) => ({
        id: item.id,
        name: item.name,
        value: String(item.name || "").trim().toLowerCase(),
      }))
      .filter((item) => item.value);

    setPaymentMethods(list);
  } catch (error) {
    console.error("Payment Methods Error:", error);

    setPaymentMethods([]);

    showSnackbar(getErrorMessage(error), "error");
  } finally {
    setLoadingPaymentMethods(false);
  }
}, [showSnackbar]);

  /* =======================================================
     FETCH EXPENSES
     GET /library/expense/list/
     params: organization, branch, category, payment_mode,
             status, expense_date_from, expense_date_to
  ======================================================= */

  const fetchExpenses = useCallback(
    async (customFilters = filters) => {
      if (!organizationId) return;

      setLoading(true);

      try {
        const params = {
          organization: Number(organizationId),
        };

        if (selectedBranchId) {
          params.branch = Number(selectedBranchId);
        }

        if (customFilters.status) {
          params.status = customFilters.status;
        }

        if (customFilters.category) {
          params.category = Number(customFilters.category);
        }

        if (customFilters.payment_mode) {
          params.payment_mode = customFilters.payment_mode;
        }

        if (customFilters.date_from) {
          params.expense_date_from = customFilters.date_from;
        }

        if (customFilters.date_to) {
          params.expense_date_to = customFilters.date_to;
        }

        const response = await api.get(EXPENSE_LIST_API, { params });

        setExpenses(getArray(response));
      } catch (error) {
        console.error("Error fetching expenses:", error);

        setExpenses([]);

        showSnackbar(getErrorMessage(error), "error");
      } finally {
        setLoading(false);

        setInitialLoading(false);
      }
    },
    [organizationId, selectedBranchId, filters, showSnackbar]
  );

  /* Re-fetch whenever the global branch or organization
     changes (also runs on mount). */

  useEffect(() => {
    if (!organizationId) return;

    fetchExpenses();

    fetchCategories();
  }, [organizationId, selectedBranchId]);

  /* =======================================================
     SEARCH — client-side filter
  ======================================================= */

  const filteredExpenses = useMemo(() => {
    let list = expenses;

    /* CATEGORY FILTER — applied client-side because the
       list endpoint may not filter by category server-side.
       Matches on the category id first, then falls back to
       the category name. */

    if (filters.category) {
      const selectedId = String(filters.category);

      const selectedCategory = categories.find(
        (item) => String(item.id) === selectedId
      );

      const selectedName = String(
        selectedCategory?.category_name || ""
      ).toLowerCase();

      list = list.filter((expense) => {
        const expenseCategoryId = String(
          expense.category ?? expense.category_id ?? ""
        );

        if (expenseCategoryId && expenseCategoryId === selectedId) {
          return true;
        }

        const expenseCategoryName = String(
          expense.category_name || ""
        ).toLowerCase();

        return Boolean(selectedName) && expenseCategoryName === selectedName;
      });
    }

    const search = searchText.trim().toLowerCase();

    if (!search) return list;

    return list.filter((expense) => {
      const expenseId = String(expense?.expense_id || "").toLowerCase();

      const paidTo = String(expense?.paid_to || "").toLowerCase();

      const enteredBy = String(expense?.entered_by || "").toLowerCase();

      const category = String(expense?.category_name || "").toLowerCase();

      const branch = String(expense?.branch_name || "").toLowerCase();

      return (
        expenseId.includes(search) ||
        paidTo.includes(search) ||
        enteredBy.includes(search) ||
        category.includes(search) ||
        branch.includes(search)
      );
    });
  }, [expenses, searchText, filters.category, categories]);

  const displayedExpenses = filteredExpenses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* =======================================================
     ACTIVE FILTER COUNT
  ======================================================= */

  const activeFilterCount = useMemo(() => {
    let count = 0;

    if (filters.category) count++;
    if (filters.payment_mode) count++;

    if (filters.status && filters.status !== "pending") count++;

    if (filters.date_from) count++;
    if (filters.date_to) count++;

    return count;
  }, [filters]);

  /* =======================================================
     VIEW DETAIL — GET /library/expense/detail/{id}/
  ======================================================= */

  const handleViewDetail = async (id) => {
    try {
      setDetailLoading(true);
      setSelectedExpense(null);
      setDetailOpen(true);

      const response = await api.get(`${EXPENSE_DETAIL_API}${id}/`);

      const detail = response?.data?.data || response?.data;

      setSelectedExpense(detail);
    } catch (error) {
      console.error("Error fetching expense detail:", error);

      setDetailOpen(false);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setDetailLoading(false);
    }
  };

  /* =======================================================
     APPROVE — PATCH /library/expense/approve/{id}/
     body: { remarks } (optional)
  ======================================================= */

  const handleOpenApprove = (expense) => {
    if (!expense || isFinalized(expense)) return;

    setSelectedExpense(expense);
    setApproveRemarks("");
    setApproveOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedExpense?.id) return;

    setActionLoading(true);

    try {
      await api.patch(`${EXPENSE_APPROVE_API}${selectedExpense.id}/`, {
        remarks: approveRemarks?.trim() || "",
      });

      showSnackbar(
        `${selectedExpense.expense_id || "Expense"} approved successfully.`,
        "success"
      );

      setApproveOpen(false);
      setDetailOpen(false);
      setSelectedExpense(null);
      setApproveRemarks("");

      await fetchExpenses();
    } catch (error) {
      console.error("Error approving expense:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setActionLoading(false);
    }
  };

  /* =======================================================
     REJECT — PATCH /library/expense/reject/{id}/
     body: { rejection_reason }
  ======================================================= */

  const handleOpenReject = (expense) => {
    if (!expense || isFinalized(expense)) return;

    setSelectedExpense(expense);
    setRejectionReason("");
    setRejectOpen(true);
  };

  const handleReject = async () => {
    if (!selectedExpense?.id) return;

    if (!rejectionReason.trim()) {
      showSnackbar("Please enter a rejection reason.", "error");
      return;
    }

    setActionLoading(true);

    try {
      await api.patch(`${EXPENSE_REJECT_API}${selectedExpense.id}/`, {
        rejection_reason: rejectionReason.trim(),
      });

      showSnackbar(
        `${selectedExpense.expense_id || "Expense"} rejected successfully.`,
        "success"
      );

      setRejectOpen(false);
      setDetailOpen(false);
      setSelectedExpense(null);
      setRejectionReason("");

      await fetchExpenses();
    } catch (error) {
      console.error("Error rejecting expense:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setActionLoading(false);
    }
  };

  /* =======================================================
     FILTER DIALOG
  ======================================================= */

  const handleOpenFilter = () => {
    setTempFilters(filters);
    setFilterOpen(true);
  };

  const handleCloseFilter = () => {
    if (loading) return;

    setTempFilters(filters);
    setFilterOpen(false);
  };

  const handleTempFilterChange = (field, value) => {
    setTempFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = async () => {
    if (
      tempFilters.date_from &&
      tempFilters.date_to &&
      tempFilters.date_from > tempFilters.date_to
    ) {
      showSnackbar("Date From cannot be greater than Date To.", "error");
      return;
    }

    setFilters(tempFilters);
    setFilterOpen(false);
    setPage(0);

    await fetchExpenses(tempFilters);
  };

  const handleClearFilters = async () => {
    setSearchText("");

    setFilters(initialFilters);
    setTempFilters(initialFilters);

    setFilterOpen(false);
    setPage(0);

    await fetchExpenses(initialFilters);
  };

  useEffect(() => {
  fetchPaymentMethods();
}, [fetchPaymentMethods]);

const handleRefresh = () => {
  fetchExpenses();
  fetchCategories();
  fetchPaymentMethods();   // ADD
};

  /* =======================================================
     PAGINATION
  ======================================================= */

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /* =======================================================
     CLOSE DETAIL
  ======================================================= */

  const handleCloseDetail = () => {
    if (actionLoading) return;

    setDetailOpen(false);
    setSelectedExpense(null);
  };

  /* =======================================================
     FORMAT HELPERS
  ======================================================= */

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) return date;

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "₹0";

    const numericAmount = Number(amount);

    if (Number.isNaN(numericAmount)) return `₹${amount}`;

    return `₹${numericAmount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getStatusColor = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "approved":
        return "success";

      case "rejected":
        return "error";

      case "pending":
      default:
        return "warning";
    }
  };

  /* Expenses that already reached a final state can no
     longer be approved or rejected. */

  const isFinalized = (expense) => {
    const status = String(expense?.status || "").toLowerCase();

    return ["approved", "rejected", "paid", "done"].includes(status);
  };

  /* =======================================================
     UI
  ======================================================= */

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        color: textPrimary,
      }}
    >
      {/* ===================================================
          PAGE HEADER
      =================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", lg: "center" },
          gap: 1.5,
          mb: 3,
          flexWrap: { xs: "wrap", lg: "nowrap" },
          width: "100%",
        }}
      >
        {/* SEARCH — client-side, live filtering */}

        <TextField
          size="small"
          variant="outlined"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            setPage(0);
          }}
          placeholder="Search expense ID, paid to, entered by..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: theme.palette.primary.main }} />
              </InputAdornment>
            ),
            endAdornment: (
              searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText("");
                      setPage(0);
                    }}
                    sx={{ color: textPrimary }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            ),
          }}
          sx={{
            flex: { xs: "1 1 100%", sm: "1 1 340px", lg: "1 1 auto" },
            minWidth: { lg: 280 },
            maxWidth: { lg: 420 },
            "& .MuiOutlinedInput-root": {
              height: 50,
              borderRadius: "30px",
              backgroundColor: inputBackground,
              color: textPrimary,
              "& input": {
                color: textPrimary,
                WebkitTextFillColor: textPrimary,
              },
              "& input::placeholder": {
                color: textPrimary,
                opacity: 0.75,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark
                  ? "rgba(255,255,255,0.28)"
                  : "rgba(0,0,0,0.22)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        {/* ACTIONS */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            flexShrink: 0,
            width: { xs: "100%", lg: "auto" },
            justifyContent: { xs: "flex-start", lg: "flex-end" },
            ml: { lg: "auto" },
          }}
        >
          {/* REFRESH */}

          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Refresh
          </Button>

          {/* FILTERS (with count badge) */}

          <Button
            variant="contained"
            startIcon={<FilterList />}
            endIcon={
              activeFilterCount > 0 ? (
                <Chip
                  label={activeFilterCount}
                  size="small"
                  sx={{
                    height: 22,
                    minWidth: 22,
                    px: 0.5,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    backgroundColor: "rgba(255,255,255,0.25)",
                    color: "#fff",
                  }}
                />
              ) : null
            }
            onClick={handleOpenFilter}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              gap: 1.5,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Filters
          </Button>

          {/* CLEAR */}

          {activeFilterCount > 0 && (
            <Button
              variant="contained"
              startIcon={<Clear />}
              onClick={handleClearFilters}
              sx={{
                height: 50,
                px: 2.5,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                boxShadow: "none",
                flexShrink: 0,
                "&:hover": { boxShadow: "none" },
              }}
            >
              Clear
            </Button>
          )}
        </Box>
      </Box>

      {/* ===================================================
          TABLE
      =================================================== */}

      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <CardContent
          sx={{
            p: 0,
            "&:last-child": { pb: 0 },
          }}
        >
          <CustomTableScrollbar
            sx={{
              overflowX: "auto",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Table stickyHeader sx={{ minWidth: 1250 }}>
              <TableHead>
                <TableRow
                 sx={{
                    "& .MuiTableCell-root": {
                      backgroundColor: theme.palette.primary.main,
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      borderBottom: "none",
                      py: 1.8,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>S.No.</TableCell>

                  <TableCell>Expense ID</TableCell>

                  <TableCell>Date</TableCell>

                  <TableCell>Category</TableCell>

                  <TableCell>Branch</TableCell>

                  <TableCell>Paid To</TableCell>

                  <TableCell>Amount</TableCell>

                  <TableCell>Payment Mode</TableCell>

                  <TableCell>Entered By</TableCell>

                  <TableCell>Status</TableCell>

                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading || initialLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      align="center"
                      sx={{ py: 7, borderBottom: "none" }}
                    >
                      <CircularProgress
                        size={32}
                        sx={{ color: theme.palette.primary.main }}
                      />

                      <Typography sx={{ mt: 1.5, color: textPrimary }}>
                        Loading expenses...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : displayedExpenses.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      align="center"
                      sx={{ py: 8, borderBottom: "none" }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          color: textPrimary,
                        }}
                      >
                        No expenses found
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ mt: 0.7, color: textPrimary }}
                      >
                        Try changing your search or filters.
                      </Typography>

                      {(searchText || activeFilterCount > 0) && (
                        <Button
                          size="small"
                          sx={{
                            mt: 1.2,
                            textTransform: "none",
                            fontWeight: 600,
                          }}
                          onClick={handleClearFilters}
                        >
                          Clear Search & Filters
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedExpenses.map((expense, index) => (
                    <TableRow key={expense.id || index} hover>
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      {/* EXPENSE ID */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Typography
                          fontWeight={700}
                          sx={{ color: textPrimary }}
                        >
                          {expense.expense_id || "-"}
                        </Typography>
                      </TableCell>

                      {/* DATE */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography sx={{ color: textPrimary }}>
                          {formatDate(expense.expense_date)}
                        </Typography>
                      </TableCell>

                      {/* CATEGORY */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Typography
                          fontWeight={600}
                          sx={{ color: textPrimary }}
                        >
                          {expense.category_name || "-"}
                        </Typography>
                      </TableCell>

                      {/* BRANCH */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography sx={{ color: textPrimary }}>
                          {expense.branch_name || "-"}
                        </Typography>
                      </TableCell>

                      {/* PAID TO */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography sx={{ color: textPrimary }}>
                          {expense.paid_to || "-"}
                        </Typography>
                      </TableCell>

                      {/* AMOUNT */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography
                          fontWeight={800}
                          sx={{ color: theme.palette.success.main }}
                        >
                          {formatAmount(expense.amount)}
                        </Typography>
                      </TableCell>

                      {/* PAYMENT MODE */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: textPrimary }}
                        >
                          {expense.payment_mode_display ||
                            expense.payment_mode ||
                            "-"}
                        </Typography>
                      </TableCell>

                      {/* ENTERED BY */}

                      <TableCell sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}>
                        <Typography
                          variant="body2"
                          sx={{ color: textPrimary }}
                        >
                          {expense.entered_by || "-"}
                        </Typography>
                      </TableCell>

                      {/* STATUS */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Chip
                          label={
                            expense.status_display ||
                            expense.status ||
                            "Pending"
                          }
                          color={getStatusColor(expense.status)}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            borderRadius: "7px",
                          }}
                        />
                      </TableCell>

                      {/* ACTIONS */}

                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          {/* VIEW */}

                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetail(expense.id)}
                              sx={{
                                width: 32,
                                height: 32,
                                color: theme.palette.info.main,
                                backgroundColor: alpha(
                                  theme.palette.info.main,
                                  0.08
                                ),
                                "&:hover": {
                                  backgroundColor: alpha(
                                    theme.palette.info.main,
                                    0.18
                                  ),
                                },
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* APPROVE */}

                          <Tooltip
                            title={
                              isFinalized(expense)
                                ? `Expense is already ${String(
                                    expense.status || ""
                                  ).toLowerCase()}`
                                : "Approve"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={actionLoading || isFinalized(expense)}
                                onClick={() => handleOpenApprove(expense)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  color: isFinalized(expense)
                                    ? theme.palette.action.disabled
                                    : theme.palette.success.main,
                                  backgroundColor: isFinalized(expense)
                                    ? "transparent"
                                    : alpha(theme.palette.success.main, 0.08),
                                  "&:hover": {
                                    backgroundColor: alpha(
                                      theme.palette.success.main,
                                      0.18
                                    ),
                                  },
                                }}
                              >
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          {/* REJECT */}

                          <Tooltip
                            title={
                              isFinalized(expense)
                                ? `Expense is already ${String(
                                    expense.status || ""
                                  ).toLowerCase()}`
                                : "Reject"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={actionLoading || isFinalized(expense)}
                                onClick={() => handleOpenReject(expense)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  color: isFinalized(expense)
                                    ? theme.palette.action.disabled
                                    : theme.palette.error.main,
                                  backgroundColor: isFinalized(expense)
                                    ? "transparent"
                                    : alpha(theme.palette.error.main, 0.08),
                                  "&:hover": {
                                    backgroundColor: alpha(
                                      theme.palette.error.main,
                                      0.18
                                    ),
                                  },
                                }}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CustomTableScrollbar>

          {/* PAGINATION — client-side */}

          <TablePagination
            component="div"
            count={filteredExpenses.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              color: textPrimary,
              borderTop: `1px solid ${borderColor}`,
              "& .MuiTablePagination-selectIcon": {
                color: textPrimary,
              },
              "& .MuiTablePagination-actions button": {
                color: textPrimary,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* ===================================================
          FILTER DIALOG
      =================================================== */}

      <Dialog
        open={filterOpen}
        onClose={handleCloseFilter}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Filter Expenses
          </Typography>

          <IconButton onClick={handleCloseFilter} sx={{ color: textPrimary }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              mt: 1,
            }}
          >
            {/* BRANCH — from global selector, read-only */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                BRANCH
              </Typography>

              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  px: 2,
                  py: 1.25,
                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: textPrimary }}
                >
                  {expenses[0]?.branch_name ||
                    currentUser?.branch_name ||
                    (selectedBranchId
                      ? `Branch ${selectedBranchId}`
                      : "-")}
                </Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{ mt: 0.5, display: "block", color: textPrimary }}
              >
                Change the branch from the global branch selector in the
                layout.
              </Typography>
            </Box>

            {/* CATEGORY */}

            <FormControl fullWidth size="small" sx={selectLabelStyles}>
              <InputLabel>Category</InputLabel>

              <Select
                value={tempFilters.category}
                label="Category"
                onChange={(event) =>
                  handleTempFilterChange("category", event.target.value)
                }
                disabled={loadingCategories}
                MenuProps={menuProps}
                sx={selectStyles}
              >
                <MenuItem value="">All Categories</MenuItem>

                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.category_name || `Category ${category.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* PAYMENT MODE */}

            <FormControl fullWidth size="small" sx={selectLabelStyles}>
              <InputLabel>Payment Mode</InputLabel>

              <Select
  value={tempFilters.payment_mode}
  label="Payment Mode"
  onChange={(event) =>
    handleTempFilterChange("payment_mode", event.target.value)
  }
  disabled={loadingPaymentMethods}
  MenuProps={menuProps}
  sx={selectStyles}
>
  <MenuItem value="">All Payment Modes</MenuItem>

  {paymentMethods.map((method) => (
    <MenuItem key={method.id} value={method.value}>
      {method.name}
    </MenuItem>
  ))}
</Select>
            </FormControl>

            {/* STATUS — server-side param */}

            <FormControl fullWidth size="small" sx={selectLabelStyles}>
              <InputLabel>Status</InputLabel>

              <Select
                value={tempFilters.status}
                label="Status"
                onChange={(event) =>
                  handleTempFilterChange("status", event.target.value)
                }
                MenuProps={menuProps}
                sx={selectStyles}
              >
                <MenuItem value="">All Statuses</MenuItem>

                <MenuItem value="pending">Pending</MenuItem>

                <MenuItem value="approved">Approved</MenuItem>

                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>

            {/* DATE RANGE — maps to expense_date_from / expense_date_to */}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                label="Date From"
                type="date"
                size="small"
                value={tempFilters.date_from}
                onChange={(event) =>
                  handleTempFilterChange("date_from", event.target.value)
                }
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyles}
              />

              <TextField
                label="Date To"
                type="date"
                size="small"
                value={tempFilters.date_to}
                onChange={(event) =>
                  handleTempFilterChange("date_to", event.target.value)
                }
                InputLabelProps={{ shrink: true }}
                sx={textFieldStyles}
              />
            </Box>
          </Box>
        </StyledDialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleClearFilters}
            disabled={loading}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              color: textPrimary,
              borderColor: theme.palette.divider,
              "&:hover": {
                borderColor: textPrimary,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={handleApplyFilters}
            disabled={loading}
            sx={{
              minWidth: 140,
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
            }}
          >
            {loading ? "Applying..." : "Apply Filters"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          DETAIL DIALOG
      =================================================== */}

      <Dialog
        open={detailOpen}
        onClose={handleCloseDetail}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: dialogPaperStyles }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Expense Details
          </Typography>

          <IconButton
            onClick={handleCloseDetail}
            disabled={actionLoading}
            sx={{ color: textPrimary }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent dividers>
          {detailLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 5,
              }}
            >
              <CircularProgress
                sx={{ color: theme.palette.primary.main }}
              />
            </Box>
          ) : selectedExpense ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2.5,
              }}
            >
              <DetailItem
                label="Expense ID"
                value={selectedExpense.expense_id}
              />

              <DetailItem
                label="Expense Date"
                value={formatDate(selectedExpense.expense_date)}
              />

              <DetailItem
                label="Organization"
                value={
                  selectedExpense.organization_name ||
                  selectedExpense.organization
                }
              />

              <DetailItem
                label="Branch"
                value={selectedExpense.branch_name || selectedExpense.branch}
              />

              <DetailItem
                label="Category"
                value={selectedExpense.category_name || selectedExpense.category}
              />

              <DetailItem
                label="Amount"
                value={formatAmount(selectedExpense.amount)}
              />

              <DetailItem label="Paid To" value={selectedExpense.paid_to} />

              <DetailItem
                label="Payment Mode"
                value={
                  selectedExpense.payment_mode_display ||
                  selectedExpense.payment_mode
                }
              />

              <DetailItem
                label="Entered By"
                value={selectedExpense.entered_by}
              />

              <DetailItem
                label="Status"
                value={
                  selectedExpense.status_display || selectedExpense.status
                }
              />

              <DetailItem
                label="Approved By"
                value={selectedExpense.approved_by || "-"}
              />

              <Box sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}>
                <DetailItem
                  label="Remarks"
                  value={selectedExpense.remarks || "-"}
                />
              </Box>

              {selectedExpense.rejection_reason && (
                <Box sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}>
                  <DetailItem
                    label="Rejection Reason"
                    value={selectedExpense.rejection_reason}
                  />
                </Box>
              )}

              {selectedExpense.attachment && (
                <Box sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: textPrimary }}
                  >
                    Attachment
                  </Typography>

                  <Button
                    variant="outlined"
                    component="a"
                    href={selectedExpense.attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Attachment
                  </Button>
                </Box>
              )}
            </Box>
          ) : null}
        </StyledDialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            color="error"
            variant="outlined"
            disabled={
              actionLoading || !selectedExpense || isFinalized(selectedExpense)
            }
            onClick={() => handleOpenReject(selectedExpense)}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
          >
            Reject
          </Button>

          <Button
            color="success"
            variant="contained"
            disabled={
              actionLoading || !selectedExpense || isFinalized(selectedExpense)
            }
            onClick={() => handleOpenApprove(selectedExpense)}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          APPROVE CONFIRMATION
      =================================================== */}

      <Dialog
        open={approveOpen}
        onClose={() => {
          if (!actionLoading) setApproveOpen(false);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(theme.palette.success.main, 0.15),
              }}
            >
              <CheckCircle color="success" />
            </Box>

            <Typography variant="h6" fontWeight={700}>
              Approve Expense
            </Typography>
          </Box>
        </DialogTitle>

        <Divider />

        <StyledDialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, color: textPrimary }}>
            Are you sure you want to approve{" "}
            <strong>{selectedExpense?.expense_id || "-"}</strong>?
          </Typography>

          {selectedExpense && (
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                mb: 2,
              }}
            >
              <InfoRow
                label="Category"
                value={selectedExpense.category_name || "-"}
              />

              <InfoRow
                label="Branch"
                value={selectedExpense.branch_name || "-"}
              />

              <InfoRow label="Paid To" value={selectedExpense.paid_to || "-"} />

              <InfoRow
                label="Amount"
                value={formatAmount(selectedExpense.amount)}
                last
              />
            </Box>
          )}

          {/* Approve remarks (optional, per Swagger body) */}

          <TextField
            fullWidth
            size="small"
            label="Remarks (Optional)"
            value={approveRemarks}
            onChange={(event) => setApproveRemarks(event.target.value)}
            placeholder="Approval remarks..."
            sx={textFieldStyles}
          />

          <Box
            sx={{
              display: "flex",
              gap: 1,
              mt: 2,
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: alpha(theme.palette.success.main, 0.1),
            }}
          >
            <WarningAmber fontSize="small" color="success" />

            <Typography
              variant="caption"
              sx={{ color: textSecondary }}
            >
              Once approved, this expense will be marked as approved and
              can no longer be edited.
            </Typography>
          </Box>
        </StyledDialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="contained"
            onClick={() => setApproveOpen(false)}
            disabled={actionLoading}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              color: textPrimary,
              borderColor: theme.palette.divider,
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={actionLoading}
            startIcon={
              actionLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CheckCircle />
              )
            }
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {actionLoading ? "Approving..." : "Yes, Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          REJECT DIALOG
      =================================================== */}

      <Dialog
        open={rejectOpen}
        onClose={() => {
          if (!actionLoading) setRejectOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Reject Expense
          </Typography>

          <IconButton
            onClick={() => setRejectOpen(false)}
            disabled={actionLoading}
            sx={{ color: textPrimary }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <StyledDialogContent dividers>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: textSecondary }}
          >
            Please provide a reason for rejecting this expense
            {selectedExpense?.expense_id
              ? ` (${selectedExpense.expense_id})`
              : ""}
            .
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(event) => setRejectionReason(event.target.value)}
            placeholder="Enter rejection reason..."
            required
            sx={textFieldStyles}
          />
        </StyledDialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setRejectOpen(false)}
            disabled={actionLoading}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              color: textPrimary,
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleReject}
            disabled={actionLoading || !rejectionReason.trim()}
            startIcon={
              actionLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Cancel />
              )
            }
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {actionLoading ? "Rejecting..." : "Reject Expense"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          SNACKBAR
      =================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* =========================================================
   DETAIL ITEM
========================================================= */

const DetailItem = ({ label, value }) => {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>

      <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.3 }}>
        {value || "-"}
      </Typography>
    </Box>
  );
};

/* =========================================================
   INFO ROW
========================================================= */

const InfoRow = ({ label, value, last = false }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        mb: last ? 0 : 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: theme.palette.text.disabled }}
      >
        {label}
      </Typography>

      <Typography variant="body2" fontWeight={600} textAlign="right">
        {value}
      </Typography>
    </Box>
  );
};

export default ExpenseApproval;